import { formatClockTime } from "@scribenote/brian-design-system";

import { formatDuration, parseMs } from "./monitorTime";
import type { RoomCounts, RoomOccupant } from "./perceptionClient";

export const whoWord = (o: RoomOccupant) => {
  if (o.displayName)
    return o.nameStatus === "candidate" ? `${o.displayName}?` : o.displayName;
  if (o.kind === "animal") return "animal";
  if (o.role === "staff") return "staff, not named";
  if (o.role === "client") return "client";
  return "person, role unknown";
};

export const patientWord = (
  visitUuid: string,
  patients: ReadonlyMap<string, string>,
) => patients.get(visitUuid) ?? `visit ${visitUuid.slice(0, 8)}`;

export const visitWord = (
  o: RoomOccupant,
  patients: ReadonlyMap<string, string>,
) => {
  if (!o.visitUuid || o.visitStatus === "none") return null;
  const patient = patientWord(o.visitUuid, patients);
  return o.visitStatus === "bound" ? patient : `${patient}?`;
};

export const phaseWord = (phase: string) => {
  const words = phase.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The browser-clock time with seconds; the raw text when it does not parse. */
export const clockWord = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : formatClockTime(date, false, true);
};

export const ago = (iso: string, now: number) => {
  const ms = parseMs(iso);
  return ms === null ? null : `${formatDuration(Math.max(0, now - ms))} ago`;
};

export const countWord = (value: string, one: string, many: string) => {
  if (value === "unclear") return `${many} unclear`;
  if (value === "3_or_more") return `3+ ${many}`;
  return `${value} ${value === "1" ? one : many}`;
};

const carriersWord = (value: string) =>
  value === "1" ? "carrier" : "carriers";

const animalsWord = (counts: RoomCounts) => {
  const animals = countWord(counts.animal, "animal", "animals");
  if (counts.in_carrier) return `${animals} in ${carriersWord(counts.animal)}`;
  const carrierSeen = counts.carrier !== "0" && counts.carrier !== "unclear";
  return carrierSeen ? `${animals} · ${carriersWord(counts.carrier)}` : animals;
};

export const countsWord = (counts: RoomCounts) =>
  `${countWord(counts.person, "person", "people")} · ${animalsWord(counts)}`;
